import { useState } from "react";
import { useNotify } from "ra-core";
import { Link, useNavigate } from "react-router";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { ESTADOS, SECTORES } from "../directory/directoryFilters";

type Found = {
  id: number;
  nombre_comercial: string | null;
  razon_social: string | null;
  municipio: string | null;
  estado: string;
};
const FIELDS: [string, string][] = [
  ["nombre_comercial", "Nombre comercial"],
  ["razon_social", "Razón social"],
  ["actividad", "Actividad"],
  ["sector", "Sector"],
  ["estado", "Estado"],
  ["municipio", "Municipio"],
  ["cp", "C.P."],
  ["direccion", "Dirección"],
  ["telefono", "Teléfono"],
  ["email", "Correo"],
  ["sitio_web", "Sitio web"],
  ["rango_personal", "Personal"],
];
const RANGOS = [
  "0 a 5 personas",
  "6 a 10 personas",
  "11 a 30 personas",
  "31 a 50 personas",
  "51 a 100 personas",
  "101 a 250 personas",
  "251 y más personas",
];
const cls = "h-9 w-full rounded-lg border bg-card px-2 text-sm";

/** "Empresa con IA" (PRD A.2): pegas un nombre o sitio web y la IA llena la ficha; tú revisas. */
export const CompanyAiDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [q, setQ] = useState("");
  const [found, setFound] = useState<Found[] | null>(null);
  const [v, setV] = useState<Record<string, string> | null>(null);
  const [meta, setMeta] = useState<{
    engine: string;
    fuentes: string[];
  } | null>(null);
  const [deep, setDeep] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const notify = useNotify();
  const navigate = useNavigate();
  const sb = getSupabaseClient();
  const reset = () => {
    setQ("");
    setFound(null);
    setV(null);
    setMeta(null);
  };
  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const search = async () => {
    const text = q.trim();
    if (!text) return;
    setBusy("buscar");
    const dom = text
      .toLowerCase()
      .replace(/^https?:\/\/(www\.)?/, "")
      .replace(/\/.*$/, "");
    const isUrl = /^[\w-]+(\.[\w-]+)+$/.test(dom) && text.includes(".");
    const base = sb
      .from("directory")
      .select("id, nombre_comercial, razon_social, municipio, estado")
      .limit(5);
    const { data } = isUrl
      ? await base.eq("dominio", dom)
      : await base.ilike("nombre_comercial", `%${text}%`);
    setBusy(null);
    setFound((data ?? []) as Found[]);
    if (!data?.length) fill();
  };

  const fill = async () => {
    setBusy("ia");
    const { data, error } = await sb.functions.invoke("ai", {
      body: {
        action: "lookup",
        query: q.trim(),
        sectores: SECTORES,
        estados: ESTADOS,
      },
    });
    setBusy(null);
    if (error || !data?.fields) {
      let msg =
        "La IA no pudo llenar la ficha. Intenta de nuevo o da de alta la empresa a mano.";
      try {
        msg =
          (await (error as { context?: Response })?.context?.json())?.message ??
          msg;
      } catch {
        /* sin detalle */
      }
      return notify(msg, { type: "error" });
    }
    const f = data.fields as Record<string, string | string[]>;
    setV(Object.fromEntries(FIELDS.map(([k]) => [k, String(f[k] ?? "")])));
    setMeta({
      engine: data.engine,
      fuentes: [
        ...((f.fuentes as string[]) ?? []),
        ...(data.sources ?? []).map((s: { url: string }) => s.url),
      ].slice(0, 5),
    });
  };

  const save = async () => {
    if (!v?.nombre_comercial.trim())
      return notify("Falta el nombre", { type: "warning" });
    setBusy("guardar");
    const { data: id, error } = await sb.rpc("directory_create", { p: v });
    if (error) {
      setBusy(null);
      return notify(`No se pudo guardar: ${error.message}`, { type: "error" });
    }
    if (deep) await sb.rpc("research_enqueue", { p_ids: [id], p_engine: null });
    setBusy(null);
    notify(
      deep
        ? "Empresa agregada al directorio; la IA ya la está investigando"
        : "Empresa agregada al directorio",
      { type: "success" },
    );
    reset();
    onClose();
    navigate(`/directory/${id}/show`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Empresa con IA</DialogTitle>
          <DialogDescription>
            Pega el nombre o el sitio web. Primero reviso si ya está en el
            directorio; si no, la IA llena la ficha y tú la revisas antes de
            guardar.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setV(null);
            search();
          }}
        >
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ej.: Tacos el Pata o www.tacoselpata.com"
            aria-label="Nombre o sitio web"
            autoFocus
          />
          <Button type="submit" disabled={!q.trim() || !!busy}>
            {busy === "buscar" ? "Buscando…" : "Buscar"}
          </Button>
        </form>

        {found && found.length > 0 && !v && (
          <div className="flex flex-col gap-1.5 rounded-lg border p-3 text-sm">
            <b>Ya está en el directorio:</b>
            {found.map((x) => (
              <Link
                key={x.id}
                to={`/directory/${x.id}/show`}
                onClick={close}
                className="text-primary hover:underline"
              >
                {x.nombre_comercial ?? x.razon_social} ·{" "}
                {[x.municipio, x.estado].filter(Boolean).join(", ")}
              </Link>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="mt-1 self-start"
              disabled={!!busy}
              onClick={fill}
            >
              <Sparkles /> No es ninguna: llenar con IA
            </Button>
          </div>
        )}
        {busy === "ia" && (
          <p className="text-sm text-muted-foreground">
            La IA está buscando la empresa… (unos 20 segundos)
          </p>
        )}

        {v && (
          <div className="flex flex-col gap-3">
            <div className="grid gap-2 sm:grid-cols-2">
              {FIELDS.map(([k, label]) => (
                <label
                  key={k}
                  className={`grid gap-1 text-sm font-semibold ${["actividad", "direccion"].includes(k) ? "sm:col-span-2" : ""}`}
                >
                  {label}
                  {k === "sector" ||
                  k === "estado" ||
                  k === "rango_personal" ? (
                    <select
                      className={cls}
                      value={v[k]}
                      onChange={(e) => setV({ ...v, [k]: e.target.value })}
                    >
                      <option value="">—</option>
                      {(k === "sector"
                        ? SECTORES
                        : k === "estado"
                          ? ESTADOS
                          : RANGOS
                      ).map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      className="h-9"
                      value={v[k]}
                      onChange={(e) => setV({ ...v, [k]: e.target.value })}
                    />
                  )}
                </label>
              ))}
            </div>
            {meta && (
              <p className="text-xs text-muted-foreground">
                Llenada con {meta.engine}. Revisa cada dato: la IA puede
                equivocarse.
                {meta.fuentes.length > 0 && (
                  <> Fuentes: {meta.fuentes.join(" · ")}</>
                )}
              </p>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-[var(--primary)]"
                checked={deep}
                onChange={(e) => setDeep(e.target.checked)}
              />
              Investigarla a fondo al guardar (expediente, match y guion)
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={close} disabled={!!busy}>
                Cancelar
              </Button>
              <Button onClick={save} disabled={!!busy}>
                {busy === "guardar" ? "Guardando…" : "Guardar en el directorio"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
